import { untilDestroyed, UntilDestroy } from '@ngneat/until-destroy';
import { Component, OnInit } from '@angular/core';
import { routerTransition } from '../../shared/animations/router.animations';
import { of } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { switchMap, take } from 'rxjs/operators';
import { UserPermissionService } from '../../user/user-permission/user-permission.service';
import { readWrite, readOnly } from '../../user/user-permission/user-permission.data';
import { EntityService } from '../entity.service';
import { MatDialog } from '@angular/material/dialog';
import { BusinessAdvisorPickerComponent } from '../../shared/business-advisor/business-advisor-picker/business-advisor-picker.component';
import { CustomerData } from '../../customer/customer.model';
import { AdvisorData, AdvisorFacade, EntityExData, HorizontalListData, MessageService } from 'common';
import { EntityProfileAdapter } from './entity-profile.data';

@UntilDestroy()
@Component({
    selector: 'ifb-entity-profile',
    templateUrl: './entity-profile.component.html',
    styleUrls: ['./entity-profile.component.scss'],
    providers: [EntityProfileAdapter],
    animations: routerTransition,
    standalone: false
})
export class EntityProfileComponent implements OnInit {
  advisorData: AdvisorData;
  data: EntityProfileComponentData;
  salesforceViewMode: boolean;
  canEditEntityInfo: boolean;
  canReadEntityInfo: boolean;
  canEditEntitySensitive: boolean;
  canViewAdvisor: boolean;
  canEditAdvisor: boolean;
  canResubmitUCC: boolean;
  canViewUCCFiling: boolean;
  statusListItems: HorizontalListData[];
  brokerListItems: HorizontalListData[];

  constructor(
    private readonly route: ActivatedRoute,
    private readonly userPermissionService: UserPermissionService,
    private readonly entityService: EntityService,
    private readonly dialog: MatDialog,
    private readonly messageService: MessageService,
    private readonly adapter: EntityProfileAdapter,
    private readonly advisorFacade: AdvisorFacade,
  ) { }

  ngOnInit(): void {
    this.route.params.pipe(untilDestroyed(this))
      .subscribe(params => this.salesforceViewMode = params.uniqueId !== undefined);

    this.route.data.pipe(untilDestroyed(this))
      .subscribe((data: EntityProfileComponentData) => {
        this.data = data;
        this.refreshAdvisorData(data?.entity?.advisorId);
        this.refreshWidgetListData(data.entity);
      });

    this.userPermissionService.granted([readWrite('servicing-entities')])
      .subscribe(res => this.canEditEntityInfo = res);

    this.userPermissionService.granted([readOnly('servicing-entities')])
      .subscribe(res => this.canReadEntityInfo = res);

    this.userPermissionService.granted([readWrite('servicing-entities-sensitive-data')])
      .subscribe(res => this.canEditEntitySensitive = res);

    this.userPermissionService.granted([readOnly('servicing-advisors-assignment')])
      .subscribe(res => this.canViewAdvisor = res);

    this.userPermissionService.granted([readWrite('servicing-advisors-assignment')])
      .subscribe(res => this.canEditAdvisor = res);

    this.userPermissionService.granted([readWrite('servicing-entity-ucc-filings')])
      .subscribe(res => this.canResubmitUCC = res);

    this.userPermissionService.granted([readOnly('servicing-entity-ucc-filings')])
      .subscribe(res => this.canViewUCCFiling = res);
  }

  private refreshAdvisorData(advisorId?: number): void {
    if (!advisorId) {
      this.advisorData = null;
      return;
    }

    this.advisorFacade.getAdvisor$(advisorId)
      .pipe(take(1))
      .subscribe((advisorData: AdvisorData) => this.advisorData = advisorData);
  }

  private refreshWidgetListData(data: EntityExData): void {
    this.statusListItems = this.adapter.adaptStatusListData(data);
    this.brokerListItems = this.adapter.adaptBrokerListData(data);
  }

  changeAdvisor(): void {
    BusinessAdvisorPickerComponent.show(this.dialog)
      .pipe(switchMap((data) => {
        if (!data || !data.id) {
          return of();
        }
        return this.entityService.updateEntityAdvisor(this.data.entity.id, data.id)
          .pipe(switchMap(() => this.entityService.get(this.data.entity.id)))
      }))
      .subscribe((data: EntityExData) => {
        if (data) {
          this.data.entity = data;
          this.refreshAdvisorData(data?.advisorId);
          this.refreshWidgetListData(data);
          this.messageService.success(`Advisor successfully changed to ${data.advisorName}`);
        }
      })
  }
}
export interface EntityProfileComponentData {
  entity: EntityExData;
  owners?: CustomerData[];
}
